<template>
  <div class="communications-table">
    <FilterBar
      cookieName="AccountantCommunicationFilterOptionsVue"
      :mainFilters="['SearchFilter']"
      filterBarType="accountant-communications"
      ref="accountant-communications-filterbar"
      @change="setupData"
    />
    <div v-if="selectableRows" class="communications-table-actions">
      <GekkoButton
        :text="$gettext('Select all')"
        :icon="allItemsSelected ? 'fa-solid fa-square-check' : 'fa-regular fa-square'"
        class="text-black-semi"
        @click="selectAllTableItems()"
        variant="light-square"
        data-analytics-click="archive select all"
      />
      <GekkoButton
        :text="isArchived ? $gettext('Unarchive') : $gettext('Archive')"
        icon="fa-solid fa-folder"
        class="text-black-semi"
        @click="archiveSelected()"
        :disabled="!selectedCommunications.length"
        variant="light-square"
        data-analytics-click="archive selected"
      />
    </div>
    <div class="content--inner">
      <overlay-scrollbars :options="scrollBarsOptions" class="communications-table-scrollbar">
        <b-table
          :items="communications"
          :fields="tableFields"
          striped
          hover
          @row-clicked="rowClicked"
          :class="['gk-table gk-table--responsive gk-table--striped gk-table--hover']"
          :busy="loading"
          show-empty
          table-busy
          ref="communications-table"
        >
          <template #empty="">
            <span class="empty-table-text" v-translate>No communications found.</span>
          </template>

          <template #table-busy="">
            <b-skeleton-table
              :rows="10"
              :columns="selectableRows ? 5 : 4"
              :table-props="{ bordered: false, striped: true }"
              :hide-header="true"
            />
          </template>

          <template v-if="selectableRows" #cell(selector)="row">
            <span class="communications-row-selector" @click.stop="toggleRowSelection(row.item)">
              <font-awesome-icon
                v-if="row.item.selected"
                class="communications-row-selector__icon is-selected"
                icon="fa-solid fa-square-check"
              />
              <font-awesome-icon v-else class="communications-row-selector__icon" icon="fa-regular fa-square" />
            </span>
          </template>

          <template #cell(recipients)="data">
            <div class="textwrap">
              <div class="textwrap--content">
                <span class="text-unassigned">{{ $gettext('To:') }}</span>
                {{ getFormattedRecipients(data.item.recipients) }}
              </div>
            </div>
          </template>

          <template #cell(subject)="data">
            <div class="textwrap">
              <div class="textwrap--content">
                {{ data.item.subject }} - <span class="text-unassigned">{{ data.item.message }}</span>
              </div>
            </div>
          </template>

          <template #cell(status)="data">
            <div v-if="data.item.is_archived" class="status-badge status-unassigned">
              {{ $gettext('Archived') }}
            </div>
            <div v-else-if="data.value === 'idle'" class="status-badge status-recurrent">
              {{ $gettext('Scheduled') }}
            </div>
            <div v-else-if="data.value === 'queued'" class="status-badge status-sending">
              {{ $gettext('Queued') }}
            </div>
            <div v-else-if="data.value === 'sent'" class="status-badge status-complete">
              {{ $gettext('Sent') }}
            </div>
            <div v-else-if="data.value === 'failed'" class="status-badge status-overdue">
              {{ $gettext('Failed') }}
            </div>
          </template>

          <template #cell(scheduled_at)="data">
            <div>
              {{ data.value | formatDateWithTime }}
            </div>
          </template>

          <template slot="bottom-row" v-if="nextUrl">
            <td :colspan="tableFields.length" class="table-load-more">
              <GekkoButton
                v-if="nextUrl && !fetchingData"
                @click="handleScroll()"
                id="load_more_button"
                class="text-info"
                variant="link"
                :text="$gettext('Load more communications')"
              />
              <LoadingSpinner v-if="fetchingData" />
            </td>
          </template>
        </b-table>
      </overlay-scrollbars>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import FilterBar from '@/components/list_filters/FilterBar'

import CommunicationService from '@/store/services/communication.service.js'
import GekkoButton from '@/components/GekkoButton'
import LoadingSpinner from '@/components/dashboard/ui/LoadingSpinner'

export default {
  name: 'CommunicationsList',
  components: {
    GekkoButton,
    FilterBar,
    LoadingSpinner,
  },
  props: {
    selectableRows: {
      type: Boolean,
      default: true,
    },
    checkedRows: {
      type: Array,
      default: () => [],
    },
    selectedReturn: {
      type: String,
      default: 'id',
    },
  },
  data() {
    return {
      communications: [],
      fields: [
        { key: 'recipients', tdClass: 'col-comm-recipients' },
        { key: 'subject', tdClass: 'col-comm-content' },
        { key: 'status', tdClass: 'col-comm-status' },
        { key: 'scheduled_at', tdClass: 'col-comm-scheduled-at' },
      ],
      loading: true,
      currentFilters: null,
      nextUrl: null,
      allItemsSelected: false,
      status: 'loading',
      fetchingData: false,
      scrollBarsOptions: {
        callbacks: {
          onScrollStop: this.handleScroll,
        },
      },
    }
  },
  computed: {
    ...mapGetters({
      currentCompany: 'accountants/getCurrentAccountantCompany',
    }),
    tableFields() {
      return this.fields
    },
    selectedCommunications() {
      if (!this.communications) return []

      if (this.selectedReturn === 'object') {
        return this.communications.filter((item) => item.selected)
      }

      return this.communications.filter((item) => item.selected).map((item) => item.id)
    },
    isArchived() {
      return this.$route.params.filter_by === 'archived'
    },
  },
  created() {
    if (this.selectableRows) {
      this.fields.unshift({
        key: 'selector',
        label: '',
        tdClass: 'col-comm-selector',
        thClass: 'col-comm-selector',
      })
    }
    window.addEventListener('scroll', this.handleScroll)

    // this.mainRowEl = document.querySelector('.main-row')

    // if (this.mainRowEl) {
    //   this.mainRowEl.addEventListener('scroll', this.handleScroll)
    // }

  },
  destroyed() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    ...mapActions({
      resendInvitation: 'accountants/resendInvitation',
    }),
    setupData(filters) {
      if (this.$route.params.id) {
        this.getCommunications(this.$route.params.id)
      }
      this.getCommunications(null, filters)
    },
    getCommunications(id, filters) {
      this.loading = true
      this.fetchingData = true
      this.status = 'loading'
      this.communications = []
      this.allItemsSelected = false

      CommunicationService.list({ ...filters, status: this.$route.params['filter_by'] })
        .then((response) => {
          this.communications = response.data.results
          this.nextUrl = response.data.next
          this.status = 'success'

          if (this.checkedRows) this.checkRows()
        })
        .catch(() => {
          this.status = 'error'
        })
        .finally(() => {
          this.loading = false
          this.fetchingData = false
        })
    },
    getFormattedRecipients(recipients_list) {
      let recipients = ''
      if (recipients_list.length > 0) {
        recipients = recipients_list.map((recipient) => recipient.name).join(', ')
      } else {
        recipients = this.$gettext('everyone')
      }
      return recipients
    },
    refreshData() {
      this.$refs['accountant-communications-filterbar'].filtersChanged()
    },
    rowClicked(item) {
      this.$router.push({
        name: 'CommunicationDetail',
        params: {
          communication_id: item.id,
        },
      })
    },
    selectAllTableItems() {
      this.allItemsSelected = !this.allItemsSelected
      this.communications.forEach((communication, communicationIndex) => {
        this.$set(this.communications, communicationIndex, {
          ...communication,
          selected: this.allItemsSelected,
        })
      })
      this.selectedTableItem()
    },
    selectedTableItem() {
      this.communications.splice()
      this.allItemsSelected = this.communications.length > 0 && this.communications.every((item) => item.selected)
      this.$emit('selected-item', this.selectedCommunications)
    },
    checkRows() {
      for (let i = 0; i < this.communications.length; i++) {
        if (this.checkedRows.includes(this.communications[i].id)) {
          this.$set(this.communications, i, {
            ...this.communications[i],
            selected: true,
          })
        }
      }

      this.allItemsSelected = this.communications.length > 0 && this.communications.every((item) => item.selected)
    },
    archiveSelected() {
      const payload = {
        is_archived: !this.isArchived,
        communication_ids: this.selectedCommunications,
      }
      console.log('Archive these communication ids:', payload)

      CommunicationService.bulkArchiveCommunication(payload).then(() => {
        this.refreshData()
      })
    },
    isInViewport(element_id) {
      console.log('isInViewport', element_id)
      var element = document.getElementById(element_id)
      console.log('isInViewport element', element)
      if (!element) return false
      var rect = element.getBoundingClientRect()
      var html = document.documentElement
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || html.clientHeight) &&
        rect.right <= (window.innerWidth || html.clientWidth)
      )
    },
    handleScroll() {
      console.log('handleScroll 1')
      if (!this.fetchingData && this.nextUrl) {
        console.log('handleScroll 2')
        const loadNewMessages = this.isInViewport('load_more_button')
        if (loadNewMessages) {
          console.log('handleScroll 3')
          this.fetchingData = true
          CommunicationService.loadNextPage(this.nextUrl).then((response) => {
            this.fetchingData = false
            this.nextUrl = response.data.next
            this.communications = this.communications.concat(response.data.results)
            this.status = 'success'
            if (this.checkedRows) this.checkRows()
          })
        }
      }
    },
    toggleRowSelection(item) {
      const communicationIndex = this.communications.findIndex((communication) => communication.id === item.id)

      this.$set(this.communications, communicationIndex, {
        ...this.communications[communicationIndex],
        selected: !item.selected,
      })

      this.selectedTableItem()
    },
  },
}
</script>

<style lang="scss">
.communications-table {
  .os-host.communications-table-scrollbar {
    min-height: 360px;
  }

  &.table-on-modal {
    .os-host.communications-table-scrollbar {
      height: 300px !important;
      max-height: 300px;

      .os-content {
        padding: 0 !important;
      }
    }
  }

  .communications-table-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding-left: 3px;
    margin-bottom: 0.25rem;

    .btn {
      .svg-inline--fa {
        font-size: 1rem;
      }
    }
  }

  .gk-table {
    .col-comm-selector {
      width: 1%;
      white-space: nowrap;
      text-align: center;
      vertical-align: middle;
      padding-left: 0.75rem;
      padding-right: 0.5rem;
    }

    .col-comm-recipients {
      width: 20%;
      text-align: left;
    }

    .col-comm-content {
      width: 50%;
      min-width: 200px;
      text-align: left;
    }

    .col-comm-status {
      width: 10%;
      text-align: right;
    }

    .col-comm-scheduled-at {
      width: 10%;
      white-space: nowrap;
      text-align: right;
    }
  }

  .communications-row-selector {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    vertical-align: middle;
    cursor: pointer;

    &__icon {
      font-size: 1rem;
      line-height: 1;

      &.is-selected {
        opacity: 1;
      }
    }
  }

  .textwrap {
    display: grid;
    width: 100%;
    min-width: 200px;

    &--content {
      overflow: hidden;
      max-width: 95%;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }
}
</style>